import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Monitor } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useHosts, useRevokeAgentKey } from "@/hooks/use-api";
import {
  formatAbsoluteTime,
  formatRelativeTime,
} from "@/lib/formatters";
import type { HostRow, HostStatus } from "@/types/api";
import { APP_ROUTES, appRouteForPathname } from "@/lib/app-routes";

const OS_LABELS: Record<string, string> = {
  windows: "Windows",
  macos: "macOS",
  linux: "Linux",
  unknown: "Unknown",
};

function osLabel(os: HostRow["os"]): string {
  if (!os) return "Unknown";
  return OS_LABELS[os] ?? "Unknown";
}

const STATUS_TEXT: Record<HostStatus, string> = {
  active: "Active",
  stale: "Stale",
  dead: "Dead",
  never_seen: "Never seen",
};

const STATUS_CLASSES: Record<HostStatus, string> = {
  active:
    "bg-green-500/15 text-green-700 ring-1 ring-green-500/30 dark:text-green-300",
  stale:
    "bg-amber-500/15 text-amber-700 ring-1 ring-amber-500/30 dark:text-amber-300",
  dead:
    "bg-red-500/15 text-red-700 ring-1 ring-red-500/30 dark:text-red-300",
  never_seen:
    "bg-muted text-muted-foreground ring-1 ring-border",
};

export function StatusChip({ status }: { status: HostStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[status]}`}
      data-testid={`status-chip-${status}`}
    >
      {STATUS_TEXT[status]}
    </span>
  );
}

export function HostsEmptyState({ integrationsRoute = APP_ROUTES.integrations }: { integrationsRoute?: string }) {
  const endpointSetupRoute = `${integrationsRoute}?add=endpoint`;
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <p className="text-sm text-muted-foreground">
        No endpoints yet.{" "}
        <Link
          to={endpointSetupRoute}
          className="font-medium text-primary hover:underline"
        >
          Add one from Integrations → Endpoint Agent.
        </Link>
      </p>
    </div>
  );
}

function HostsTableSkeleton() {
  return (
    <div className="space-y-3" data-testid="hosts-skeleton">
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

function LabelCell({ row }: { row: HostRow }) {
  // Treat blank/whitespace-only labels the same as null — bare-minted
  // keys can carry an empty string and "Unlabeled host" is more
  // informative than rendering an empty primary line.
  const primary = labelFor(row);
  return (
    <div className="flex flex-col">
      <span className="font-medium">{primary}</span>
      <code className="mt-0.5 w-fit rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
        {row.key_prefix}…
      </code>
    </div>
  );
}

function TimeCell({ iso }: { iso: string | null }) {
  if (!iso) {
    return <span className="text-muted-foreground">Never</span>;
  }
  return (
    <span title={formatAbsoluteTime(iso)} className="text-muted-foreground">
      {formatRelativeTime(iso)}
    </span>
  );
}

const REVOKE_CONFIRM =
  "Revoke this agent key? The host using it will stop ingesting immediately. This cannot be undone.";
const REVOKE_FALLBACK_ERROR = "Failed to revoke agent key.";
const REVOKE_ROLES = ["owner", "admin"] as const;

function labelFor(row: HostRow): string {
  return row.instance_label?.trim() || "Unlabeled host";
}

export default function HostsPage() {
  const location = useLocation();
  const { data, isPending, isError, refetch } = useHosts();
  const revoke = useRevokeAgentKey();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canRevoke =
    user?.roles?.some((r) =>
      REVOKE_ROLES.includes(r as (typeof REVOKE_ROLES)[number]),
    ) ?? false;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  async function handleRevoke(id: string) {
    if (!window.confirm(REVOKE_CONFIRM)) return;
    setErrorMessage(null);
    setPendingId(id);
    try {
      await revoke.mutateAsync(id);
      // useRevokeAgentKey invalidates ["agent","keys"] but not ["me","hosts"];
      // refresh the table + AppShell chip in one go. Awaited so the
      // disabled-button state lifts only once the next render shows
      // the revoked row gone.
      await qc.invalidateQueries({ queryKey: ["me", "hosts"] });
    } catch (err) {
      const msg =
        err instanceof Error && err.message
          ? err.message
          : REVOKE_FALLBACK_ERROR;
      setErrorMessage(msg);
    } finally {
      setPendingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Hosts</h1>
        <p className="text-muted-foreground">
          Production and test endpoint enrollments for this tenant. Status
          reflects recent authenticated agent activity.
        </p>
      </div>

      {errorMessage && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {errorMessage}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Endpoints</CardTitle>
        </CardHeader>
        <CardContent>
          {isPending && <HostsTableSkeleton />}

          {!isPending && isError && (
            <div
              role="alert"
              className="flex flex-col items-center justify-center gap-3 py-12 text-center"
            >
              <p className="text-sm text-muted-foreground">
                Couldn’t load hosts.
              </p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          )}

          {!isPending && !isError && data && data.hosts.length === 0 && (
            <HostsEmptyState
              integrationsRoute={appRouteForPathname(location.pathname, APP_ROUTES.integrations)}
            />
          )}

          {!isPending && !isError && data && data.hosts.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead>OS</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[100px]">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.hosts.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <LabelCell row={row} />
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-2 text-sm">
                        <Monitor className="h-4 w-4 text-muted-foreground" />
                        {osLabel(row.os)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={row.status} />
                    </TableCell>
                    <TableCell>
                      <TimeCell iso={row.last_used_at} />
                    </TableCell>
                    <TableCell>
                      <TimeCell iso={row.created_at} />
                    </TableCell>
                    <TableCell>
                      {canRevoke && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-destructive hover:text-destructive"
                          // Disable ALL Revoke buttons while any one is
                          // in flight — matches agents-tab pattern.
                          // Without this, clicking row B while A is
                          // pending starts a second mutation before A
                          // resolves, and pendingId's "last write wins"
                          // can leave A's button visually re-enabled.
                          disabled={pendingId !== null}
                          aria-label={`Revoke agent key for ${labelFor(row)}`}
                          onClick={() => handleRevoke(row.id)}
                        >
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
